import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ArrowRight, Cloud, Server, HardDrive, Globe,
  Zap, ShieldCheck, Database, Headphones, Lock, Wrench,
  Cpu, Network, Send,
} from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { DomainSearch } from "@/components/sections/domain-search";
import { CTABanner } from "@/components/sections/cta-banner";
import { ProductGrid } from "@/components/storefront/product-grid";
import { fetchProductsByKind } from "@/lib/storefront";
import { TopRelatedDigitalItems } from "@/components/sections/top-related-digital-items";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/hosting/")({
  component: HostingPage,
  head: () => ({
    meta: [
      { title: "Hosting — Shared, VPS, Cloud & Dedicated — CoderLity" },
      { name: "description", content: "Lightning-fast hosting: shared, WordPress, VPS, cloud, dedicated servers and domains. Free SSL, daily backups, 24/7 support." },
      { property: "og:title", content: "Hosting — CoderLity" },
      { property: "og:description", content: "From shared hosting to bare-metal — NVMe, LiteSpeed, free SSL and 99.99% uptime." },
      { property: "og:url", content: "/hosting" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/hosting" }],
  }),
});

type Sub = { label: string; to: string; desc: string };
type Cat = {
  slug: string;
  label: string;
  to: string;
  tagline: string;
  icon: typeof Cloud;
  gradient: string;
  subs: Sub[];
};

const CATEGORIES: Cat[] = [
  {
    slug: "web",
    label: "Web Hosting",
    to: "/hosting/web",
    tagline: "Fast, secure hosting for any website — NVMe + LiteSpeed.",
    icon: Cloud,
    gradient: "from-sky-500/20 to-blue-500/5",
    subs: [
      { label: "Shared Hosting", to: "/hosting/web/shared-hosting", desc: "Starter-friendly shared plans" },
      { label: "WordPress Hosting", to: "/hosting/web/wordpress-hosting", desc: "Managed WP with staging" },
      { label: "Reseller Hosting", to: "/hosting/web/reseller-hosting", desc: "White-label hosting resell" },
      { label: "Free SSL", to: "/hosting/web/free-ssl", desc: "Auto-renewing SSL" },
      { label: "Daily Backups", to: "/hosting/web/daily-backups", desc: "30-day retention" },
      { label: "24/7 Support", to: "/hosting/web/support-247", desc: "Real engineers, any time" },
    ],
  },
  {
    slug: "vps-cloud",
    label: "VPS & Cloud",
    to: "/hosting/vps-cloud",
    tagline: "Scalable performance for production workloads.",
    icon: Server,
    gradient: "from-purple-500/20 to-indigo-500/5",
    subs: [
      { label: "VPS Hosting", to: "/hosting/vps-cloud/vps-hosting", desc: "Predictable dedicated VMs" },
      { label: "Cloud VPS", to: "/hosting/vps-cloud/cloud-vps", desc: "Hourly cloud instances" },
      { label: "Managed VPS", to: "/hosting/vps-cloud/managed-vps", desc: "We run the ops for you" },
      { label: "Cloud Hosting", to: "/hosting/vps-cloud/cloud-hosting", desc: "Multi-AZ, auto-healing" },
      { label: "Auto-scaling", to: "/hosting/vps-cloud/auto-scaling", desc: "Scale on real signals" },
      { label: "Load Balancing", to: "/hosting/vps-cloud/load-balancing", desc: "L4/L7 with health checks" },
    ],
  },
  {
    slug: "dedicated-servers",
    label: "Dedicated Servers",
    to: "/hosting/dedicated-servers",
    tagline: "Raw power, total control — bare metal & GPU.",
    icon: HardDrive,
    gradient: "from-orange-500/20 to-rose-500/5",
    subs: [
      { label: "Bare Metal", to: "/hosting/dedicated-servers/bare-metal", desc: "Single-tenant hardware" },
      { label: "GPU Servers", to: "/hosting/dedicated-servers/gpu-servers", desc: "AI training & inference" },
      { label: "Storage Servers", to: "/hosting/dedicated-servers/storage-servers", desc: "High-density HDD/NVMe" },
      { label: "DDoS Protection", to: "/hosting/dedicated-servers/ddos-protection", desc: "Always-on mitigation" },
      { label: "Server Management", to: "/hosting/dedicated-servers/server-management", desc: "Patching & monitoring" },
    ],
  },
  {
    slug: "domains",
    label: "Domains",
    to: "/hosting/domains",
    tagline: "Search, register, transfer and manage every TLD.",
    icon: Globe,
    gradient: "from-emerald-500/20 to-teal-500/5",
    subs: [
      { label: "Domain Registration", to: "/hosting/domains/domain-registration", desc: "All TLDs, transparent pricing" },
      { label: "Domain Transfer", to: "/hosting/domains/domain-transfer", desc: "Concierge bulk transfers" },
      { label: "Bulk Search", to: "/hosting/domains/bulk-search", desc: "Find brandable names" },
      { label: "WHOIS Lookup", to: "/hosting/domains/whois-lookup", desc: "Bulk WHOIS + expiry alerts" },
      { label: "DNS Management", to: "/hosting/domains/dns-management", desc: "Anycast DNS with DNSSEC" },
      { label: "Domain Privacy", to: "/hosting/domains/domain-privacy", desc: "Hide WHOIS contact details" },
    ],
  },
];

const SPOTLIGHT: { icon: typeof Zap; label: string; desc: string; to: string }[] = [
  { icon: Zap, label: "NVMe + LiteSpeed", desc: "20× faster than legacy SATA hosting.", to: "/hosting/web/shared-hosting" },
  { icon: ShieldCheck, label: "Free SSL + WAF", desc: "Auto-renewing SSL on every plan.", to: "/hosting/web/free-ssl" },
  { icon: Database, label: "Daily Backups", desc: "30-day retention, one-click restore.", to: "/hosting/web/daily-backups" },
  { icon: Headphones, label: "24/7 Support", desc: "Real engineers respond in <15 min.", to: "/hosting/web/support-247" },
  { icon: Lock, label: "Domain Privacy", desc: "Hide WHOIS contact details.", to: "/hosting/domains/domain-privacy" },
  { icon: Wrench, label: "Managed Ops", desc: "We run patching & monitoring.", to: "/hosting/dedicated-servers/server-management" },
  { icon: Cpu, label: "GPU Servers", desc: "H100/A100 for AI workloads.", to: "/hosting/dedicated-servers/gpu-servers" },
  { icon: Network, label: "Load Balancers", desc: "L4/L7 with sticky sessions.", to: "/hosting/vps-cloud/load-balancing" },
  { icon: Send, label: "Free Migration", desc: "White-glove move from any host.", to: "/hosting/web/shared-hosting" },
];

function HostingPage() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState<string>("web");

  const { data: hosting = [], isLoading: l1 } = useQuery({
    queryKey: ["storefront", "products", "hosting"],
    queryFn: () => fetchProductsByKind("hosting"),
  });
  const { data: vps = [], isLoading: l2 } = useQuery({
    queryKey: ["storefront", "products", "vps"],
    queryFn: () => fetchProductsByKind("vps"),
  });

  const activeCat = CATEGORIES.find((c) => c.slug === active) ?? CATEGORIES[0];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return null;
    return CATEGORIES.flatMap((c) =>
      c.subs
        .filter((s) => s.label.toLowerCase().includes(needle) || s.desc.toLowerCase().includes(needle))
        .map((s) => ({ ...s, catLabel: c.label, catSlug: c.slug })),
    ).slice(0, 24);
  }, [q]);

  return (
    <PageShell>
      <PageHero
        eyebrow="Hosting"
        title="Hosting built for performance"
        sub="Shared, WordPress, VPS, cloud, dedicated & domains — NVMe, LiteSpeed, free SSL and a 99.99% uptime SLA."
      />

      {/* Search */}
      <section className="pb-4 pt-2">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search hosting plans, features and domain tools…"
              className="h-12 pl-11 text-sm"
              aria-label="Search hosting"
            />
          </div>
          <AnimatePresence>
            {filtered && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="mt-3 grid gap-2 rounded-2xl border border-border bg-card p-3 sm:grid-cols-2"
              >
                {filtered.length === 0 ? (
                  <div className="col-span-2 px-2 py-3 text-sm text-muted-foreground">No matches.</div>
                ) : (
                  filtered.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to as never}
                      className="group flex items-start gap-3 rounded-lg p-2 transition hover:bg-secondary"
                    >
                      <ArrowRight className="mt-1 h-3.5 w-3.5 text-primary transition group-hover:translate-x-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{s.label}</div>
                        <div className="truncate text-xs text-muted-foreground">{s.catLabel} · {s.desc}</div>
                      </div>
                    </Link>
                  ))
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Category tabs */}
      <section className="py-10">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-6 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const isActive = c.slug === active;
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setActive(c.slug)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                    isActive
                      ? "border-primary/40 bg-primary/10 text-foreground"
                      : "border-border bg-card text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {c.label}
                </button>
              );
            })}
          </div>

          <motion.div
            key={activeCat.slug}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={`overflow-hidden rounded-3xl border border-border bg-gradient-to-br ${activeCat.gradient} p-6 sm:p-8`}
          >
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                  <activeCat.icon className="h-3.5 w-3.5 text-primary" />
                  {activeCat.label}
                </div>
                <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                  {activeCat.label}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground sm:text-base">{activeCat.tagline}</p>
              </div>
              <Link
                to={activeCat.to as never}
                className="inline-flex items-center gap-1.5 self-start rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-glow transition hover:opacity-90"
              >
                View overview <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {activeCat.subs.map((s) => (
                <Link
                  key={s.to}
                  to={s.to as never}
                  className="group flex items-start gap-3 rounded-xl border border-border bg-background/70 p-4 backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-card"
                >
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <activeCat.icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-semibold">{s.label}</h3>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{s.desc}</p>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Spotlight */}
      <section className="py-10">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight">Included on every plan</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SPOTLIGHT.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.label}
                  to={s.to as never}
                  className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-primary/40 hover:shadow-card"
                >
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-base font-semibold">{s.label}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{s.desc}</p>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Storefront product grids (existing) */}
      <section className="py-10">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 space-y-12">
          <div>
            <h2 className="mb-6 text-2xl font-bold tracking-tight">Ready-to-buy Web Hosting</h2>
            {l1 ? <div className="text-muted-foreground">Loading…</div> : (
              <ProductGrid products={hosting} emptyMessage="Hosting plans coming soon." />
            )}
          </div>
          <div>
            <h2 className="mb-6 text-2xl font-bold tracking-tight">Ready-to-buy VPS & Cloud</h2>
            {l2 ? <div className="text-muted-foreground">Loading…</div> : (
              <ProductGrid products={vps} emptyMessage="VPS plans coming soon." />
            )}
          </div>
        </div>
      </section>

      <DomainSearch />

      <TopRelatedDigitalItems context="hosting" />

      <CTABanner />
    </PageShell>
  );
}
